import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Close, Edit, Save } from '@mui/icons-material'
import { Box, Dialog, DialogContent, DialogTitle, IconButton, TextField, Typography } from '@mui/material'
import PropTypes from 'prop-types'

import SaveNotesDialog from './SaveNotesDialog'

const classes = {
    icon: {
        fontSize: '16px',
    },
}

const ItemNotesModal = ({ handleUpdate, miscItem = null, onClose, open, quoteItem = null }) => {
    const { t } = useTranslation()

    const [isEditingVendorNotes, setIsEditingVendorNotes] = useState(false)
    const [isEditingPrivateNotes, setIsEditingPrivateNotes] = useState(false)

    const [itemVendorNotes, setItemVendorNotes] = useState(quoteItem?.vendorNotes || miscItem?.vendorNotes)
    const [itemPrivateNotes, setItemPrivateNotes] = useState(quoteItem?.privateNotes || miscItem?.privateNotes)
    const [showConfirmationDialog, setShowConfirmationDialog] = useState(false)

    const name = quoteItem?.name || miscItem?.name

    const toggleEditVendorNotes = () => {
        const baseObj = quoteItem || miscItem

        if (isEditingVendorNotes) {
            handleUpdate({ ...baseObj, vendorNotes: itemVendorNotes }, false)
        }
        setIsEditingVendorNotes((v) => !v)
    }
    const toggleEditPrivateNotes = () => {
        const baseObj = quoteItem || miscItem

        if (isEditingPrivateNotes) {
            handleUpdate({ ...baseObj, privateNotes: itemPrivateNotes }, false)
        }
        setIsEditingPrivateNotes((v) => !v)
    }

    const handleVendorNotesChange = (event) => {
        const { value } = event.target
        if (value != itemVendorNotes) {
            setItemVendorNotes(value)
        }
    }

    const handlePrivateNotesChange = (event) => {
        const { value } = event.target
        if (value != itemPrivateNotes) {
            setItemPrivateNotes(value)
        }
    }

    const handleClose = (_, reason) => {
        if (reason !== 'backdropClick') {
            if (isEditingPrivateNotes || isEditingVendorNotes) {
                setShowConfirmationDialog(true)
            } else {
                onClose()
            }
        }
    }
    const onSaveNotesDialogCancel = () => {
        setShowConfirmationDialog(false)
        onClose()
    }

    const onSaveNotesDialogConfirm = () => {
        const baseObj = quoteItem || miscItem
        setShowConfirmationDialog(false)
        handleUpdate({ ...baseObj, privateNotes: itemPrivateNotes, vendorNotes: itemVendorNotes }, false)
        onClose()
    }

    return (
        <Dialog
            key={`item-notes-modal-${quoteItem?.id || miscItem?.id}`}
            open={open}
            slotProps={{
                paper: {
                    sx: {
                        width: 400,
                    },
                },
            }}
            disableEscapeKeyDown
            onClose={handleClose}
        >
            <DialogTitle
                alignItems="center"
                data-testid="quote-item-notes-modal"
                display="flex"
                justifyContent="space-between"
            >
                <Typography
                    sx={{
                        textTransform: 'uppercase',
                    }}
                    variant="strong1"
                >
                    {t('NOTES FOR')} {name}
                </Typography>
                <IconButton
                    color="secondary"
                    size="small"
                    onClick={handleClose}
                >
                    <Close />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 3,
                    }}
                >
                    {quoteItem ? (
                        <Box>
                            <Typography
                                data-testid="customer-notes-label"
                                variant="strong2"
                            >
                                {t('Customer notes')}
                            </Typography>
                            <TextField
                                color="secondary"
                                data-testid="customer-notes"
                                helperText={t('These notes are provided by your customers using the Web Store.')}
                                slotProps={{
                                    input: {
                                        readOnly: true,
                                        style: { fontSize: 14 },
                                    },
                                }}
                                value={quoteItem?.customerNotes}
                                fullWidth
                                multiline
                            />
                        </Box>
                    ) : null}
                    <Box>
                        <Box
                            sx={{
                                alignItems: 'center',
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'space-between',
                            }}
                        >
                            <Typography
                                data-testid="vendor-notes-label"
                                variant="strong2"
                            >
                                {t('Vendor notes')}
                            </Typography>
                            <IconButton
                                color="secondary"
                                size="small"
                                onClick={toggleEditVendorNotes}
                            >
                                {isEditingVendorNotes ? <Save sx={classes.icon} /> : <Edit sx={classes.icon} />}
                            </IconButton>
                        </Box>
                        <TextField
                            color="secondary"
                            data-testid="vendor-notes"
                            helperText={t(
                                'These notes will be visible on the Quote, Order Confirmation, Work Order and Delivery Docket.'
                            )}
                            slotProps={{
                                input: {
                                    readOnly: !isEditingVendorNotes,
                                    style: { fontSize: 14 },
                                },
                            }}
                            value={itemVendorNotes}
                            fullWidth
                            multiline
                            onChange={handleVendorNotesChange}
                        />
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                        }}
                    >
                        <Box>
                            <Box
                                sx={{
                                    alignItems: 'center',
                                    display: 'flex',
                                    flexDirection: 'row',
                                    justifyContent: 'space-between',
                                }}
                            >
                                <Typography
                                    data-testid="private-notes-label"
                                    variant="strong2"
                                >
                                    {t('Private notes')}
                                </Typography>
                                <IconButton
                                    color="secondary"
                                    size="small"
                                    onClick={toggleEditPrivateNotes}
                                >
                                    {isEditingPrivateNotes ? <Save sx={classes.icon} /> : <Edit sx={classes.icon} />}
                                </IconButton>
                            </Box>
                        </Box>
                        <TextField
                            color="secondary"
                            data-testid="private-notes"
                            helperText={t('These notes will be visible on the Work Order only.')}
                            slotProps={{
                                input: {
                                    readOnly: !isEditingPrivateNotes,
                                    style: { fontSize: 14 },
                                },
                            }}
                            value={itemPrivateNotes}
                            fullWidth
                            multiline
                            onChange={handlePrivateNotesChange}
                        />
                    </Box>
                </Box>
            </DialogContent>
            <SaveNotesDialog
                open={showConfirmationDialog}
                onCancel={onSaveNotesDialogCancel}
                onConfirm={onSaveNotesDialogConfirm}
            />
        </Dialog>
    )
}

ItemNotesModal.propTypes = {
    handleUpdate: PropTypes.func,
    miscItem: PropTypes.object,
    open: PropTypes.bool,
    quoteItem: PropTypes.object,
    onClose: PropTypes.func,
}

export default ItemNotesModal
